import {
	type UseQueryResult,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import addressProvider from "src/providers/address-provider";
import type { MerchantAddress, MerchantAddressParams } from "src/types";
import { sleep } from "src/utils";

const addressQueries = {
	listAddresses: (): UseQueryResult<MerchantAddress[]> => {
		const { merchantId } = useActiveMerchantId();

		return useQuery<MerchantAddress[]>({
			queryKey: ["listAddresses", merchantId ?? "no-merchant"],
			queryFn: () => {
				if (!merchantId) {
					return Promise.reject(new Error("Merchant ID is required"));
				}
				return addressProvider.listAddresses(merchantId);
			},
			staleTime: Infinity,
			enabled: Boolean(merchantId),
			retry: 2,
		});
	},

	createAddress: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: (params: MerchantAddressParams) => {
				if (!merchantId) {
					return Promise.reject(new Error("Merchant ID is required"));
				}
				return addressProvider.createAddress(merchantId, params);
			},
			onSuccess: async () => {
				await sleep(1000);
				queryClient.invalidateQueries({
					queryKey: ["listAddresses", merchantId ?? "no-merchant"],
				});
			},
		});
	},

	updateAddress: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: (params: MerchantAddress) => {
				if (!merchantId) {
					return Promise.reject(new Error("Merchant ID is required"));
				}
				return addressProvider.updateAddress(
					merchantId,
					params.id,
					params.name,
				);
			},
			onSuccess: async () => {
				await sleep(1000);
				queryClient.invalidateQueries({
					queryKey: ["listAddresses", merchantId ?? "no-merchant"],
				});
			},
		});
	},

	deleteAddress: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: (addressId: string) => {
				if (!merchantId) {
					return Promise.reject(new Error("Merchant ID is required"));
				}
				return addressProvider.deleteAddress(merchantId, addressId);
			},
			onSuccess: async () => {
				await sleep(1000);
				queryClient.invalidateQueries({
					queryKey: ["listAddresses", merchantId ?? "no-merchant"],
				});
			},
		});
	},
};

export default addressQueries;
